import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/swiper-bundle.css'
import {
  Pagination,
  Autoplay,
  Navigation,
  Keyboard,
  A11y,
} from 'swiper/modules'
import { Card } from '@/components/ui/card'
import { Quote, User2 } from 'lucide-react'
import clsx from 'clsx'

export type Testimonial = {
  image?: string
  name: string
  role?: string
  text: string
}

export default function MogesappTestimonialSection({
  title = 'Ce que disent nos clients',
  subtitle = 'Découvrez comment MOGESAPP simplifie la gestion des commerces au quotidien.',
  items,
  className,
}: {
  title?: string
  subtitle?: string
  items: Testimonial[]
  className?: string
}) {
  return (
    <section
      id='temoignages'
      className={clsx(
        'py-16 md:py-20 border-t bg-muted/20 dark:bg-muted/10',
        className
      )}
      aria-labelledby='testi-heading'
    >
      <div className='container mx-auto max-w-7xl px-4'>
        <div className='text-center mb-10 md:mb-14'>
          <h2
            id='testi-heading'
            className='text-3xl md:text-4xl font-extrabold tracking-tight'
          >
            {title}
          </h2>
          <p className='text-lg text-muted-foreground max-w-3xl mx-auto mt-2'>
            {subtitle}
          </p>
        </div>

        <Swiper
          modules={[Pagination, Autoplay, Navigation, Keyboard, A11y]}
          spaceBetween={24}
          slidesPerView={1}
          breakpoints={{
            640: { slidesPerView: 1 },
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
          }}
          pagination={{ clickable: true }}
          navigation
          keyboard={{ enabled: true }}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          a11y={{ enabled: true }}
        >
          {items.map((t, i) => (
            <SwiperSlide key={`${t.name}-${i}`}>
              <Card className='p-6 md:p-8 bg-background shadow-sm'>
                <div className='flex flex-col items-center gap-6'>
                  {/* avatar */}
                  <div className='flex-shrink-0'>
                    {t.image ? (
                      <img
                        src={t.image}
                        alt={`Photo de ${t.name}`}
                        className='w-24 h-24 md:w-28 md:h-28 rounded-full object-cover object-top border-4 border-primary/40'
                        loading='lazy'
                        decoding='async'
                      />
                    ) : (
                      <div className='w-24 h-24 md:w-28 md:h-28 rounded-full border-4 border-primary/40 grid place-items-center bg-muted'>
                        <User2 className='w-12 h-12 text-muted-foreground' />
                      </div>
                    )}
                  </div>

                  {/* quote */}
                  <div className='w-full'>
                    <div className='text-primary/90 text-3xl md:text-4xl mb-3 text-center'>
                      <Quote aria-hidden='true' />
                    </div>
                    <p className='text-base md:text-lg text-foreground/90 italic text-center'>
                      {t.text}
                    </p>

                    <div className='text-center mt-5'>
                      <p className='font-semibold'>{t.name}</p>
                      {t.role ? (
                        <p className='text-sm text-muted-foreground'>
                          {t.role}
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </section>
  )
}
